// El paso 1 de un modo en MOBILE: título, bajada, portada y UNA card al pie —
// ADR-2026-09-08-todos-los-modos-abren-igual-en-mobile.
//
// Es la anatomía del modo Modelo, que se fue llevando de a un modo por vez (Catálogo,
// Fotos de producto y el atajo Editar la tomaron antes) hasta quedar en TODOS los modos
// que abren eligiendo algo. Los que viven adentro de `PresetDetailSheet` la arman con las
// piezas de ese sheet; los que tienen ruta propia —Animar, Carrusel, los atajos— la arman
// con este componente, que es lo único que compartían: la forma.
//
// Antes cada uno de esos tres dibujaba su carátula a sangre sobre gris y, encima, un panel
// blanco redondeado con ícono, título, bajada y uno o dos CTA azules. Tres pantallas de
// entrada distintas para la misma pregunta ("¿con qué imagen trabajamos?"), y el par de
// botones no mostraba nada de lo que ya había.
//
// La card NO lleva miniaturas de lo ELEGIDO, a diferencia de la de productos: en estos
// modos el paso 1 es la única pantalla que hay antes de tener la imagen, así que la card
// siempre se dibuja vacía. Lleva el ícono del modo, que es lo que la conecta con la
// carátula de arriba.
//
// Sí puede llevar, en cambio, miniaturas de lo que HAY del otro lado (`miniaturas`): los
// últimos productos del usuario, como la card de Modelo. No son una selección —no hay
// tilde ni marco— sino la prueba de que hay catálogo esperando; el texto de al lado
// sigue pidiendo elegir.

import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const PasoUnoMobile: React.FC<{
  /** La consigna del paso ("Elegí la imagen a editar"). */
  titulo: string;
  /** Qué va a pasar con lo que se elija. */
  bajada: string;
  /**
   * La carátula del modo, con sus clases ya puestas: puede ser `<img>` o `<video>`, y
   * cada modo sabe cuál es la suya. Se dibuja contenida y centrada, no a sangre.
   */
  portada: React.ReactNode;
  /** Ícono del modo: va en el recuadro de la card. Lo reemplaza `miniaturas` si viene. */
  Icono: LucideIcon;
  /**
   * Qué va a la IZQUIERDA del texto de la card en vez del recuadro con el ícono — hoy,
   * el abanico con los últimos productos del usuario. Sin esto, o con un abanico vacío,
   * manda el ícono: la card no puede quedarse sin nada de ese lado, que es lo que le da
   * su forma y su alto.
   */
  miniaturas?: React.ReactNode;
  /** Texto de la card. */
  cardTexto: string;
  /** Abre la pantalla donde se elige (el sheet de generadas). */
  onAbrir: () => void;
  /**
   * Seguir al paso 2 SIN volver a elegir. Se dibuja como botón azul debajo de la card,
   * y sólo cuando el modo lo pasa —o sea, cuando ya hay algo elegido—.
   *
   * Es la salida del paso 1 para quien volvió atrás: sin esto, la única forma de
   * avanzar es abrir el picker y volver a tocar lo que ya estaba elegido.
   */
  onSiguiente?: () => void;
  /** Errores u otros avisos, entre la portada y la card. */
  aviso?: React.ReactNode;
}> = ({
  titulo,
  bajada,
  portada,
  Icono,
  cardTexto,
  onAbrir,
  aviso,
  miniaturas,
  onSiguiente,
}) => (
  // El chrome del shell dibuja el back y los créditos FLOTANDO arriba, y el contenido
  // tiene que arrancar debajo de ellos. Ese chrome mide `3.5rem` MÁS el safe area del
  // notch: con los `pt-16` (64px fijos) que había acá, en un teléfono con notch —donde
  // el chrome mide ~115px— el título se metía debajo del botón de volver
  // (ADR-2026-09-14-la-pantalla-de-mobile-arranca-debajo-del-chrome). Abajo, el safe
  // area del home indicator, que `pb-6` tampoco contemplaba.
  <div
    className="md:hidden fixed inset-0 z-30 bg-white flex flex-col px-5"
    style={{
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
      paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
    }}
  >
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
      <div className="shrink-0 text-center max-w-[340px] mb-8">
        <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
          {titulo}
        </h2>
        <p className="text-[14px] text-[#3C3C43]/60 leading-snug mt-1">{bajada}</p>
      </div>
      {portada}
    </div>
    {aviso}
    {/* `mt-auto`: la card baja al pie en vez de quedar pegada a la portada. Con margen y
        no con `justify-end`, por lo mismo que en Modelo: si el contenido crece más que la
        pantalla, `justify-end` deja la primera parte inalcanzable y el margen colapsa solo
        (ADR-2026-09-04-modelo-mobile-la-card-de-productos-baja-al-pie). */}
    <button
      type="button"
      onClick={onAbrir}
      className="mt-auto w-full h-[72px] shrink-0 rounded-2xl px-3 flex items-center gap-3 text-left bg-black/[0.035] hover:bg-black/[0.06] transition-colors"
    >
      {miniaturas ?? (
        <span className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-xl bg-white">
          <Icono className="h-5 w-5 text-brand-primary" />
        </span>
      )}
      <span className="flex-1 min-w-0 text-[15px] font-semibold text-brand-black">
        {cardTexto}
      </span>
      <ChevronRight className="w-5 h-5 shrink-0 text-brand-black/30" />
    </button>
    {onSiguiente && (
      <button
        type="button"
        onClick={onSiguiente}
        className="mt-2 w-full shrink-0 inline-flex items-center justify-center py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        Siguiente
      </button>
    )}
  </div>
);
